/**
 * @module Storage
 * @private
 * @since 2.0.0
 * @requires memoizer
 * @requires env
 */

import { memoizer } from '../../mixin/memoizer';
import { env } from '../../env';

/**
 * Common interface for localStorage and sessionStorage
 */
export class Storage {
    constructor(key) {
        memoizer(this, 'available');

        try {
            this.storage = env.browser && window[key];
        } catch (e) { /**/ }

        [
            'get',
            'set',
        ].forEach((fn) => this[fn] = this[fn].bind(this));
    }

    /**
     * available Is localStorage available
     * @type {Boolean}
     */
    get available() {
        if (!env.browser || !this.storage) {
            return false;
        }

        const key = Math.random().toString(36).substring(2);

        try {
            this.storage.setItem(key, true);
            this.storage.removeItem(key);
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * S.get
     * @param  {String} key           Key name
     * @return {Any} Value. If value can be parsed as JSON it'll be parsed as JSON
     */
    get(key) {
        if (!this.available) {
            return;
        }

        const value = this.storage.getItem(key);

        try {
            return JSON.parse(value);
        } catch (error) {
            return value;
        }
    }

    /**
     * S.set
     * @param  {String} key   Key name
     * @param  {Any}    value Value to store
     * @return {Promise}
     */
    set(key, value) {
        if (!this.available) {
            return new Promise((resolve) => resolve);
        }

        return new Promise((resolve, reject) => {
            try {
                this.storage.setItem(
                    key,
                    JSON.stringify(value)
                );
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * S.remove
     * @param {String} key Key name
     * no return value
     */
    remove(key) {
        this.available && this.storage.removeItem(key);
    }

    /**
     * S.size
     * @param  {...String} [keys] When omitted calculates all
     * @return {Object}           map of sizes. Includes "total"
     */
    size(...keys) {
        const all = !keys.length;
        let total = 0;
        all && (keys = Array.from(Array(this.storage.length)).map((item, index) => this.storage.key(index)));

        const result = keys.reduce((result, key) => {
            const length = (key + this.storage.getItem(key)).length;

            result[key] = length;
            all && (total += length);

            return result;
        }, {});

        all && Object.assign(result, {total});

        return result;
    }
}
